/**
 * Share link tokens: three words, like donkey-butter-kite.
 *
 * The token is the whole capability — it is the path of the public projection
 * — so it has to be unguessable. It used to be 192 random bits, which nobody
 * could read out across a garage or type from an email into a laptop at the
 * pit wall. Three words from a 7,772-word list is about 470 billion links:
 * far beyond guessing for something that reveals one team's dashboard over
 * public timing and expires a week after its event, and each guess is a read
 * on our bill long before it could succeed.
 */

/** How many words a token has. */
export const shareTokenWords = 3

/**
 * A token of `count` words, chosen uniformly.
 *
 * `random(n)` must return a uniform integer in [0, n) from a secure source;
 * see uniformIndex.
 */
export function wordToken(words: readonly string[], random: (n: number) => number, count = shareTokenWords): string {
  if (words.length < 2) throw new Error('a word token needs a word list')
  return Array.from({ length: count }, () => words[random(words.length)]).join('-')
}

/**
 * A uniform integer in [0, n) from crypto.getRandomValues, by rejection, so no
 * word is likelier than another: plain modulo of a 32-bit value favours the
 * low indexes slightly whenever n does not divide 2³².
 */
export function uniformIndex(n: number, fill: (a: Uint32Array) => Uint32Array = (a) => crypto.getRandomValues(a)): number {
  const limit = Math.floor(0x1_0000_0000 / n) * n
  const buf = new Uint32Array(1)
  for (;;) {
    const v = fill(buf)[0]!
    if (v < limit) return v % n
  }
}

const wordShape = new RegExp(`^[a-z]+(-[a-z]+){${shareTokenWords - 1}}$`)

/**
 * The token a typed-in link means.
 *
 * Words are forgiving: "Donkey Butter Kite", "donkey.butter.kite" and
 * "donkey_butter-kite" all mean donkey-butter-kite. Anything that does not come
 * out as words is returned exactly as given, because the older random tokens
 * are case-sensitive and must not be lowercased into somebody else's.
 */
export function canonicalShareToken(raw: string): string {
  let decoded = raw
  try {
    decoded = decodeURIComponent(raw)
  } catch {
    // Not URI-encoded after all; use it as it came.
  }
  const words = decoded
    .trim()
    .toLowerCase()
    .replace(/[\s._+]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
  return wordShape.test(words) ? words : raw
}
